import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { authToken } from '@/hooks/use-auth'
import { useI18n } from '@/i18n/i18n-react'
import type { Locales } from '@/i18n/i18n-types'
import { isLocale } from '@/i18n/i18n-util'
import { decodeInviteJwtPayload } from '@/util/decode-invite-jwt'
import { inviteRoleLabel } from '@/util/invite-role-label'

export const Route = createFileRoute('/invite')({
	component: InviteAcceptance,
	validateSearch: (search: Record<string, unknown>) => ({
		token: (search.token as string) || '',
		locale: (search.locale as string) || ''
	})
})

function InviteAcceptance() {
	const { L, setLocale } = useI18n()
	const { token, locale } = Route.useSearch()

	useEffect(() => {
		if (locale && isLocale(locale)) {
			setLocale(locale as Locales)
		}
	}, [locale, setLocale])

	if (!token) {
		return (
			<div className='flex min-h-screen items-center justify-center bg-gray-50'>
				<div className='w-full max-w-md space-y-8'>
					<div className='text-center'>
						<Heading level='h1'>{L.Auth.InvalidInvite()}</Heading>
						<p className='text-muted-foreground mt-2 text-sm'>{L.Auth.InvalidInviteDescription()}</p>
					</div>
				</div>
			</div>
		)
	}

	return <InviteAcceptanceForm key={token} token={token} />
}

function InviteAcceptanceForm({ token }: { token: string }) {
	const { L } = useI18n()
	const { api } = useApiContext()
	const navigate = useNavigate()
	const decodedInvite = useMemo(() => decodeInviteJwtPayload(token), [token])
	const roleDisplay = useMemo(() => inviteRoleLabel(decodedInvite?.role, L), [decodedInvite?.role, L])

	const form = useAppForm({
		defaultValues: {
			email: decodedInvite?.email ?? '',
			assignedRole: roleDisplay,
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: ''
		},
		onSubmit: async ({ value }) => {
			if (value.password !== value.confirmPassword) {
				toast.error(L.Auth.PasswordsDoNotMatch())
				return
			}

			try {
				const response = await api.register({
					body: {
						inviteToken: token,
						firstName: value.firstName,
						lastName: value.lastName,
						password: value.password
					}
				})

				authToken.next(response.token)
				toast.success(L.Auth.InviteAccepted())
				navigate({ to: '/analytics' })
			} catch (error: any) {
				if (error.response?.status === 403) {
					toast.error(L.Auth.InviteExpired())
				} else {
					toast.error(error.message || 'Failed to accept invite')
				}
			}
		}
	})

	return (
		<div className='flex min-h-screen items-center justify-center bg-gray-50'>
			<div className='w-full max-w-md space-y-8'>
				<div className='text-center'>
					<Heading level='h1'>{L.Auth.AcceptInvite()}</Heading>
					<p className='text-muted-foreground mt-2 text-sm'>{L.Auth.AcceptInviteDescription()}</p>
				</div>

				<Container>
					<ContainerHeader heading={L.Auth.SetPassword()} />
					<div className='p-6'>
						<form.AppForm>
							<FormContainer>
								<form
									className='block w-full'
									noValidate
									onSubmit={e => {
										e.preventDefault()
										void form.handleSubmit()
									}}
								>
									<div className='space-y-4'>
										<FormFieldGroup>
											<form.AppField
												name='email'
												children={field => (
													<field.TextField
														readOnly
														disabled
														type='text'
														autoComplete='off'
														label={L.Fields.Email()}
													/>
												)}
											/>
										</FormFieldGroup>

										<FormFieldGroup>
											<form.AppField
												name='assignedRole'
												children={field => (
													<field.TextField readOnly disabled label={L.Fields.Role()} />
												)}
											/>
										</FormFieldGroup>

										<FormFieldGroup>
											<form.AppField
												name='firstName'
												children={field => (
													<field.TextField
														label={L.Fields.FirstName?.() || 'First Name'}
														placeholder='John'
													/>
												)}
											/>
										</FormFieldGroup>

										<FormFieldGroup>
											<form.AppField
												name='lastName'
												children={field => (
													<field.TextField
														label={L.Fields.LastName?.() || 'Last Name'}
														placeholder='Doe'
													/>
												)}
											/>
										</FormFieldGroup>

										<FormFieldGroup>
											<form.AppField
												name='password'
												children={field => (
													<field.TextField
														label={L.Fields.Password()}
														type='password'
														placeholder={L.Auth.PasswordPlaceholder()}
													/>
												)}
											/>
										</FormFieldGroup>

										<FormFieldGroup>
											<form.AppField
												name='confirmPassword'
												children={field => (
													<field.TextField
														label={L.Fields.ConfirmPassword()}
														type='password'
														placeholder={L.Auth.ConfirmPasswordPlaceholder()}
													/>
												)}
											/>
										</FormFieldGroup>

										<Button
											type='submit'
											className='w-full'
											disabled={form.state.isSubmitting}
											isLoading={form.state.isSubmitting}
										>
											{L.Auth.AcceptInviteAndSetPassword()}
										</Button>
									</div>
								</form>
							</FormContainer>
						</form.AppForm>
					</div>
				</Container>
			</div>
		</div>
	)
}
